import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Plus, X, CheckCircle, Send, FileText, Download } from "lucide-react";
import { LineItemsEditor, StatusBadge, formatCurrency, formatDate } from "./QuotationShared";

// ─── View Dialog ───
export function QuoteViewDialog({ open, onOpenChange, selected, selectedLines, canEdit, onApprove, onUpdateStatus, onExport, canDownload }) {
  if (!selected) return null;
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-white rounded-sm max-w-4xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="text-xl font-bold uppercase" style={{ fontFamily: "Chivo, sans-serif" }}>
            {selected.quote_number}
          </DialogTitle>
        </DialogHeader>
        <div className="space-y-4" data-testid="quote-view-detail">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            <div className="p-3 bg-slate-50 border border-slate-200 rounded-sm">
              <p className="text-xs text-slate-500 uppercase font-bold">Client</p>
              <p className="text-sm font-medium text-slate-900 mt-1">{selected.client?.company_name || "-"}</p>
            </div>
            <div className="p-3 bg-slate-50 border border-slate-200 rounded-sm">
              <p className="text-xs text-slate-500 uppercase font-bold">Status</p>
              <div className="mt-1"><StatusBadge status={selected.commercial_status} /></div>
            </div>
            <div className="p-3 bg-slate-50 border border-slate-200 rounded-sm">
              <p className="text-xs text-slate-500 uppercase font-bold">Grand Total</p>
              <p className="text-sm font-bold text-slate-900 mt-1">{formatCurrency(selected.grand_total)}</p>
            </div>
            <div className="p-3 bg-slate-50 border border-slate-200 rounded-sm">
              <p className="text-xs text-slate-500 uppercase font-bold">Valid Until</p>
              <p className="text-sm text-slate-700 mt-1">{formatDate(selected.valid_until)}</p>
            </div>
          </div>

          {selected.subject && (
            <div className="p-3 bg-slate-50 border border-slate-200 rounded-sm">
              <p className="text-xs text-slate-500 uppercase font-bold">Subject</p>
              <p className="text-sm text-slate-700 mt-1">{selected.subject}</p>
            </div>
          )}

          <div className="border border-slate-200 rounded-sm overflow-hidden">
            <table className="w-full text-sm" data-testid="quote-lines-table">
              <thead className="bg-slate-100">
                <tr>
                  <th className="text-left px-3 py-2 text-xs font-bold text-slate-600 uppercase">#</th>
                  <th className="text-left px-3 py-2 text-xs font-bold text-slate-600 uppercase">Part</th>
                  <th className="text-left px-3 py-2 text-xs font-bold text-slate-600 uppercase">Name</th>
                  <th className="text-right px-3 py-2 text-xs font-bold text-slate-600 uppercase">Qty</th>
                  <th className="text-right px-3 py-2 text-xs font-bold text-slate-600 uppercase">Price</th>
                  <th className="text-right px-3 py-2 text-xs font-bold text-slate-600 uppercase">GST%</th>
                  <th className="text-right px-3 py-2 text-xs font-bold text-slate-600 uppercase">Total</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {selectedLines.map((l, i) => (
                  <tr key={l.id || `line-${i}`} className="hover:bg-slate-50">
                    <td className="px-3 py-2 text-slate-400">{l.line_number || i + 1}</td>
                    <td className="px-3 py-2 font-mono text-xs text-slate-700">{l.part_number_raw || "-"}</td>
                    <td className="px-3 py-2 text-slate-800">{l.part_name || "-"}</td>
                    <td className="px-3 py-2 text-right text-slate-700">{l.qty}</td>
                    <td className="px-3 py-2 text-right text-slate-700">{formatCurrency(l.unit_price)}</td>
                    <td className="px-3 py-2 text-right text-slate-500">{l.gst_rate}%</td>
                    <td className="px-3 py-2 text-right font-semibold text-slate-900">{formatCurrency(l.total_amount)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <div className="flex justify-end">
            <div className="w-64 space-y-1 text-sm">
              <div className="flex justify-between text-slate-600"><span>Subtotal:</span><span>{formatCurrency(selected.subtotal)}</span></div>
              <div className="flex justify-between text-slate-600"><span>GST:</span><span>{formatCurrency(selected.total_gst)}</span></div>
              <div className="flex justify-between font-bold text-slate-900 border-t border-slate-200 pt-1"><span>Grand Total:</span><span>{formatCurrency(selected.grand_total)}</span></div>
            </div>
          </div>

          {(selected.customer_notes || selected.internal_notes) && (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              {selected.customer_notes && (
                <div className="p-3 bg-blue-50 border border-blue-200 rounded-sm">
                  <p className="text-xs text-blue-700 uppercase font-bold mb-1">Customer Notes</p>
                  <p className="text-sm text-blue-800 whitespace-pre-wrap">{selected.customer_notes}</p>
                </div>
              )}
              {selected.internal_notes && (
                <div className="p-3 bg-amber-50 border border-amber-200 rounded-sm">
                  <p className="text-xs text-amber-700 uppercase font-bold mb-1">Internal Notes</p>
                  <p className="text-sm text-amber-800 whitespace-pre-wrap">{selected.internal_notes}</p>
                </div>
              )}
            </div>
          )}

          {canEdit && (
            <div className="flex flex-wrap gap-2 pt-2 border-t border-slate-200" data-testid="quote-action-buttons">
              {(selected.commercial_status === "draft" || selected.commercial_status === "DRAFT") && selected.internal_approval_status !== "APPROVED" && (
                <Button onClick={() => onApprove(selected.id)} data-testid="approve-quote-btn" className="bg-green-600 hover:bg-green-700 text-white rounded-sm uppercase font-bold text-xs">
                  <CheckCircle size={14} className="mr-1.5" /> Approve
                </Button>
              )}
              {(selected.commercial_status === "draft" || selected.commercial_status === "DRAFT") && (
                <Button onClick={() => onUpdateStatus(selected.id, "SENT")} data-testid="send-quote-btn" className="bg-blue-600 hover:bg-blue-700 text-white rounded-sm uppercase font-bold text-xs">
                  <Send size={14} className="mr-1.5" /> Mark as Sent
                </Button>
              )}
              {selected.commercial_status === "SENT" && (
                <>
                  <Button onClick={() => onUpdateStatus(selected.id, "ACCEPTED")} data-testid="accept-quote-btn" className="bg-green-600 hover:bg-green-700 text-white rounded-sm uppercase font-bold text-xs">
                    <CheckCircle size={14} className="mr-1.5" /> Accept
                  </Button>
                  <Button onClick={() => onUpdateStatus(selected.id, "REJECTED")} data-testid="reject-quote-btn" variant="outline" className="border-red-300 text-red-600 hover:bg-red-50 rounded-sm uppercase font-bold text-xs">
                    <X size={14} className="mr-1.5" /> Reject
                  </Button>
                </>
              )}
              {selected.commercial_status === "ACCEPTED" && (
                <Button onClick={() => onUpdateStatus(selected.id, "INVOICED")} data-testid="invoice-quote-btn" className="bg-purple-600 hover:bg-purple-700 text-white rounded-sm uppercase font-bold text-xs">
                  <FileText size={14} className="mr-1.5" /> Mark Invoiced
                </Button>
              )}
              {!["CANCELLED", "INVOICED"].includes(selected.commercial_status) && (
                <Button onClick={() => onUpdateStatus(selected.id, "CANCELLED")} variant="outline" data-testid="cancel-quote-btn" className="border-slate-300 text-slate-600 hover:bg-slate-50 rounded-sm uppercase font-bold text-xs">
                  Cancel Quote
                </Button>
              )}
              {canDownload && (
                <div className="ml-auto flex gap-1">
                  <Button size="sm" variant="outline" onClick={() => onExport(selected.id, "pdf")} data-testid="view-export-pdf" className="text-xs rounded-sm"><FileText size={14} className="mr-1" /> PDF</Button>
                  <Button size="sm" variant="outline" onClick={() => onExport(selected.id, "excel")} data-testid="view-export-excel" className="text-xs rounded-sm"><Download size={14} className="mr-1" /> Excel</Button>
                </div>
              )}
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}

// ─── Create Dialog ───
export function QuoteCreateDialog({ open, onOpenChange, form, setForm, formLines, updateLine, addLine, removeLine, calcLineTotal, calcFormTotal, clientSearch, searchClients, clients, saving, onSubmit }) {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-white rounded-sm max-w-5xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="text-xl font-bold uppercase" style={{ fontFamily: "Chivo, sans-serif" }}>New Quotation</DialogTitle>
        </DialogHeader>
        <div className="space-y-4">
          <div>
            <Label className="text-xs font-bold uppercase text-slate-600">Client *</Label>
            <Input value={clientSearch} onChange={(e) => searchClients(e.target.value)} placeholder="Search client by name..." className="mt-1 rounded-sm border-slate-300" data-testid="client-search-input" />
            {clients.length > 0 && (
              <div className="mt-1 border border-slate-200 rounded-sm bg-white max-h-40 overflow-y-auto" data-testid="client-dropdown">
                {clients.map((c) => (
                  <button key={c.id} onClick={() => { setForm({ ...form, client_id: c.id }); searchClients.clear?.(c.company_name); }}
                    className={`w-full text-left px-3 py-2 text-sm hover:bg-slate-50 border-b border-slate-100 ${form.client_id === c.id ? "bg-blue-50" : ""}`} data-testid={`client-option-${c.id}`}>
                    <span className="font-medium">{c.company_name}</span>
                    {c.contact_person && <span className="text-slate-400 text-xs ml-2">({c.contact_person})</span>}
                  </button>
                ))}
              </div>
            )}
          </div>
          <QuoteFormFields form={form} setForm={setForm} />
          <LineItemsEditor lines={formLines} updateLine={updateLine} addLine={addLine} removeLine={removeLine} calcLineTotal={calcLineTotal} />
          <div className="flex items-center justify-between pt-2 border-t border-slate-200">
            <p className="text-sm font-bold text-slate-900">Estimated Total: {formatCurrency(calcFormTotal())}</p>
            <div className="flex gap-2">
              <Button variant="outline" onClick={() => onOpenChange(false)} className="rounded-sm uppercase font-bold">Cancel</Button>
              <Button onClick={onSubmit} disabled={saving} data-testid="save-new-quote-btn" className="bg-slate-900 text-white hover:bg-slate-800 rounded-sm uppercase font-bold">
                {saving ? "Saving..." : "Create Quotation"}
              </Button>
            </div>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

// ─── Edit Dialog ───
export function QuoteEditDialog({ open, onOpenChange, selected, form, setForm, formLines, updateLine, addLine, removeLine, calcLineTotal, calcFormTotal, saving, onSubmit }) {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-white rounded-sm max-w-5xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="text-xl font-bold uppercase" style={{ fontFamily: "Chivo, sans-serif" }}>Edit {selected?.quote_number}</DialogTitle>
        </DialogHeader>
        <div className="space-y-4">
          <QuoteFormFields form={form} setForm={setForm} prefix="edit" />
          <LineItemsEditor lines={formLines} updateLine={updateLine} addLine={addLine} removeLine={removeLine} calcLineTotal={calcLineTotal} />
          <div className="flex items-center justify-between pt-2 border-t border-slate-200">
            <p className="text-sm font-bold text-slate-900">Estimated Total: {formatCurrency(calcFormTotal())}</p>
            <div className="flex gap-2">
              <Button variant="outline" onClick={() => onOpenChange(false)} className="rounded-sm uppercase font-bold">Cancel</Button>
              <Button onClick={onSubmit} disabled={saving} data-testid="save-edit-quote-btn" className="bg-slate-900 text-white hover:bg-slate-800 rounded-sm uppercase font-bold">
                {saving ? "Saving..." : "Save Changes"}
              </Button>
            </div>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

// ─── Shared Form Fields ───
function QuoteFormFields({ form, setForm, prefix = "quote" }) {
  return (
    <>
      <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
        <div>
          <Label className="text-xs font-bold uppercase text-slate-600">Subject</Label>
          <Input value={form.subject} onChange={(e) => setForm({ ...form, subject: e.target.value })} className="mt-1 rounded-sm border-slate-300" data-testid={`${prefix}-subject-input`} />
        </div>
        <div>
          <Label className="text-xs font-bold uppercase text-slate-600">Machine Model</Label>
          <Input value={form.machine_model} onChange={(e) => setForm({ ...form, machine_model: e.target.value })} className="mt-1 rounded-sm border-slate-300" data-testid={`${prefix}-machine-input`} />
        </div>
        <div>
          <Label className="text-xs font-bold uppercase text-slate-600">Valid Days</Label>
          <Input type="number" value={form.valid_days} onChange={(e) => setForm({ ...form, valid_days: parseInt(e.target.value) || 30 })} className="mt-1 rounded-sm border-slate-300" data-testid={`${prefix}-valid-days-input`} />
        </div>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        <div>
          <Label className="text-xs font-bold uppercase text-slate-600">Customer Notes</Label>
          <Textarea value={form.customer_notes} onChange={(e) => setForm({ ...form, customer_notes: e.target.value })} className="mt-1 rounded-sm border-slate-300" rows={2} data-testid={`${prefix}-customer-notes`} />
        </div>
        <div>
          <Label className="text-xs font-bold uppercase text-slate-600">Internal Notes</Label>
          <Textarea value={form.internal_notes} onChange={(e) => setForm({ ...form, internal_notes: e.target.value })} className="mt-1 rounded-sm border-slate-300" rows={2} data-testid={`${prefix}-internal-notes`} />
        </div>
      </div>
    </>
  );
}
